import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Upload, Download, Search, Folder, FolderOpen, File, Plus, Trash2, Eye, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const DocumentLibraryPage = () => {
  const [folders, setFolders] = useState([
    { id: '1', name: 'Policies & Procedures', documents: 24, lastModified: '01/25/2026' },
    { id: '2', name: 'Forms & Templates', documents: 45, lastModified: '01/24/2026' },
    { id: '3', name: 'Training Materials', documents: 18, lastModified: '01/23/2026' },
    { id: '4', name: 'Compliance Documents', documents: 12, lastModified: '01/22/2026' },
    { id: '5', name: 'Patient Education', documents: 32, lastModified: '01/21/2026' },
  ]);

  const [documents, setDocuments] = useState([
    { id: '1', name: 'HIPAA Privacy Policy.pdf', folder: 'Policies & Procedures', size: '245 KB', uploadedBy: 'Admin', uploadDate: '01/25/2026' },
    { id: '2', name: 'OASIS-E Assessment Guide.pdf', folder: 'Training Materials', size: '1.2 MB', uploadedBy: 'Clinical Director', uploadDate: '01/24/2026' },
    { id: '3', name: 'Patient Consent Form.docx', folder: 'Forms & Templates', size: '85 KB', uploadedBy: 'Admin', uploadDate: '01/23/2026' },
    { id: '4', name: 'Medication Administration Policy.pdf', folder: 'Policies & Procedures', size: '320 KB', uploadedBy: 'Clinical Director', uploadDate: '01/22/2026' },
    { id: '5', name: 'Fall Prevention Education.pdf', folder: 'Patient Education', size: '450 KB', uploadedBy: 'RN Supervisor', uploadDate: '01/21/2026' },
    { id: '6', name: 'Emergency Preparedness Plan.pdf', folder: 'Compliance Documents', size: '890 KB', uploadedBy: 'Admin', uploadDate: '01/20/2026' },
  ]);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = !selectedFolder || doc.folder === selectedFolder;
    return matchesSearch && matchesFolder;
  });

  const handleUpload = () => {
    toast.success('Document uploaded successfully');
    setShowUploadModal(false);
  };

  const handleDownload = (doc) => {
    toast.info(`Downloading ${doc.name}...`);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    setDocuments(prev => prev.filter(d => d.id !== id));
    toast.success('Document deleted');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="document-library-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#e74c3c] rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Document Library</h1>
              <p className="text-gray-600">Manage agency documents, forms, and templates</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline"><Plus className="w-4 h-4 mr-2" /> New Folder</Button>
            <Button className="bg-[#e74c3c] hover:bg-[#c0392b]" onClick={() => setShowUploadModal(true)}>
              <Upload className="w-4 h-4 mr-2" /> Upload Document
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Folders Sidebar */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-4">Folders</h3>
            <div className="space-y-2">
              <button
                onClick={() => setSelectedFolder(null)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                  !selectedFolder ? 'bg-red-50 text-red-700' : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Folder className="w-4 h-4" />
                All Documents
                <span className="ml-auto text-xs text-gray-500">{documents.length}</span>
              </button>
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => setSelectedFolder(folder.name)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    selectedFolder === folder.name ? 'bg-red-50 text-red-700' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {selectedFolder === folder.name ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
                  {folder.name}
                  <span className="ml-auto text-xs text-gray-500">{folder.documents}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Documents List */}
          <div className="lg:col-span-3 space-y-4">
            {/* Search */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search documents..." className="pl-10" />
                </div>
              </div>
            </div>

            {/* Documents Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Document Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Folder</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Size</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Uploaded By</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredDocuments.map((doc) => (
                      <tr key={doc.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{doc.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{doc.folder}</td>
                        <td className="px-4 py-3 text-gray-600">{doc.size}</td>
                        <td className="px-4 py-3 text-gray-600">{doc.uploadedBy}</td>
                        <td className="px-4 py-3 text-gray-600">{doc.uploadDate}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => toast.info(`Viewing ${doc.name}`)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="View">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDownload(doc)} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded" title="Download">
                              <Download className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(doc.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-md mx-4">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold">Upload Document</h3>
              </div>
              <div className="p-4 space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Drag & drop files here or click to browse</p>
                  <input type="file" className="hidden" />
                </div>
                <div>
                  <Label>Select Folder</Label>
                  <select className="w-full px-3 py-2 border rounded-lg">
                    {folders.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
                  <Button className="bg-[#e74c3c] hover:bg-[#c0392b]" onClick={handleUpload}>Upload</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DocumentLibraryPage;
